import { css } from '@emotion/core';
import { Button, Theme, Typography, useTheme } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useAgent } from '../../hooks/use-agent';

import { SearchResultsProvider, useSearchResults } from '../../hooks/use-agent-search-draft';
import { usePageDialog } from '../../hooks/use-page-dialog';
import { formatCountsSummary } from '../../utils/format-search-counts';
import { ProfilePreviewSkeleton } from '../profile-preview-skeleton';
import { AgentResultsErrorBoundary } from './agent-results-error-boundary';
import { AgentSearchPreview } from './agent-search-preview';
import { SearchResultsList } from './search-results-list';

const DEFAULT_VISIBLE_COUNT = 3;
const MAX_VISIBLE_COUNT = 10;

const feedStyles = (theme: Theme) => css`
    background: ${theme.palette.background.paper};
    border: 1px solid ${theme.palette.divider};
    border-radius: 8px;
    max-width: 800px;

    .search-feed__header {
        padding: 16px;
        border-bottom: 1px solid ${theme.palette.divider};
    }

    .search-feed__count {
        font-size: 18px;
        font-weight: 600;
    }

    .search-feed__content {
        padding: 0 16px;

        .profile-preview {
            padding: 24px 0;
            border-bottom: 1px solid ${theme.palette.divider};

            &:last-of-type {
                border-bottom: none;
            }

            .section .section-icon .MuiAvatar-root {
                justify-self: flex-end;
            }
        }
    }

    .search-feed__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-top: 1px solid ${theme.palette.divider};
    }

    .search-feed__saved {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        gap: 8px;

        .search-feed__saved-icon {
            color: ${theme.palette.success.main};
            font-size: 20px;
        }

        .search-feed__saved-text {
            font-size: 14px;
            color: ${theme.palette.text.secondary};
            flex: 1;
        }
    }
`;

export const SearchPreviewFeed: React.FC = () => {
    const theme = useTheme<Theme>();
    const { showDialog } = usePageDialog();
    const [visibleCount, setVisibleCount] = React.useState(DEFAULT_VISIBLE_COUNT);
    const { profiles, counts, loading, error, resultsExpired, searchDraftId, searchId, jobId } = useSearchResults();
    const { conversationId } = useAgent();

    const totalCount = counts?.total ?? 0;
    const hasProfiles = profiles.length > 0 && !resultsExpired;
    const canToggle = profiles.length > DEFAULT_VISIBLE_COUNT;
    const searchUrl = searchId && jobId ? `/job/${jobId}/search/${searchId}/total` : null;

    const handleSeeAll = () => {
        showDialog(
            <AgentResultsErrorBoundary>
                <SearchResultsProvider searchDraftId={searchDraftId}>
                    <AgentSearchPreview />
                </SearchResultsProvider>
            </AgentResultsErrorBoundary>,
            `/orbit/chat/${conversationId}`,
            'replace'
        );
    };

    const handleToggleLimit = () => {
        setVisibleCount((prev) => (prev === DEFAULT_VISIBLE_COUNT ? MAX_VISIBLE_COUNT : DEFAULT_VISIBLE_COUNT));
    };

    // Show saved state when search has been saved
    if (searchId && searchUrl) {
        return (
            <div css={feedStyles(theme)}>
                <div className="search-feed__saved">
                    <CheckCircleIcon className="search-feed__saved-icon" />
                    <Typography className="search-feed__saved-text">
                        Search saved ({totalCount.toLocaleString()} candidates)
                    </Typography>
                    <Link to={searchUrl}>
                        <Button color="primary" size="small">
                            Open Search
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    if (loading && profiles.length === 0 && !resultsExpired) {
        return (
            <div css={feedStyles(theme)}>
                <div className="search-feed__content">
                    <ProfilePreviewSkeleton count={DEFAULT_VISIBLE_COUNT} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div css={feedStyles(theme)}>
                <div className="search-feed__content">
                    <Typography color="error" variant="body2">
                        {error.message || 'Failed to load search results'}
                    </Typography>
                </div>
            </div>
        );
    }

    if (profiles.length === 0 && !resultsExpired) {
        return (
            <div css={feedStyles(theme)}>
                <div className="search-feed__content">
                    <Typography variant="body2">No profiles found</Typography>
                </div>
            </div>
        );
    }

    const header = hasProfiles ? (
        <div className="search-feed__header">
            <Typography className="search-feed__count">{formatCountsSummary(counts, 'candidates found')}</Typography>
        </div>
    ) : null;

    const seeMoreButton = canToggle ? (
        <Button color="primary" size="small" variant="text" onClick={handleToggleLimit}>
            {visibleCount === DEFAULT_VISIBLE_COUNT ? 'See more' : 'See less'}
        </Button>
    ) : null;

    const footer = hasProfiles ? (
        <div className="search-feed__footer">
            {seeMoreButton}
            <Button color="primary" onClick={handleSeeAll} size="small" variant="text">
                See all results
            </Button>
        </div>
    ) : null;

    return (
        <div css={feedStyles(theme)}>
            {header}
            <div className="search-feed__content">
                <SearchResultsList limit={visibleCount} />
            </div>
            {footer}
        </div>
    );
};
